<template>
  <container v-ssr-class class="special-orders-details">
    <zaproo-breadcrumbs class="special-orders-details-breadcrumbs">
      <zaproo-breadcrumb :to="home()">
        {{ $t("Home") }}
      </zaproo-breadcrumb>
      <zaproo-breadcrumb :to="specialOrders()">
        {{ $t("Special orders") }}
      </zaproo-breadcrumb>
      <zaproo-breadcrumb
        v-if="!pending && pageData?.title"
        :to="specialOrdersDetails(pageData || {})"
      >
        {{ pageData?.title }}
      </zaproo-breadcrumb>
    </zaproo-breadcrumbs>
    <special-orders-details-page-skeleton v-if="pending" />
    <div v-else-if="!!pageData" class="special-orders-details-main">
      <sf-button
        role="button"
        variant="tertiary"
        tag="router-link"
        :to="specialOrders()"
        class="py-2 !pl-0 !pr-3 !text-neutral-500 lg:!pl-1"
      >
        <icons-icon-chevron-left />
        <span class="text-base">{{ $t("Back") }}</span>
      </sf-button>
      <page-heading v-if="pageData?.title" class="special-orders-details-title">
        {{ pageData?.title }}
      </page-heading>
      <strapi-components-media-responsive-image
        v-if="pageData?.banner"
        class="w-full"
        :image="pageData.banner"
        :preload="{ fetchPriority: 'high' }"
        loading="eager"
        sizes="xs:100vw md:100vw lg:100vw"
      />
      <div class="special-orders-details-content">
        <special-order-form-portal
          v-if="pageData"
          class="flex-1"
          :special-order="pageData"
        />
        <our-works-slider
          v-if="pageData?.ourWorks"
          class="special-orders-details-our-works"
          :images="pageData.ourWorks.data.map((i) => i.attributes)"
        />
      </div>
    </div>
  </container>
</template>

<script setup lang="ts">
import SpecialOrdersDetailsPageSkeleton from "../components/SpecialOrdersDetailsPageSkeleton.vue";
import type { StrapiSpecialOrders } from "~/types/strapi";
import type { StrapiItemLocalizations } from "~/types/strapi/base/StrapiItemLocalizations";
import OurWorksSlider from "~/modules/special-orders/components/OurWorksSlider.vue";
import type { SeoPageMeta } from "~/modules/seo-pack/types/SeoPageMeta";

type SpecialOrderAttributes = StrapiSpecialOrders &
  StrapiItemLocalizations<StrapiSpecialOrders> & { locale: string };

const route = useRoute();
const nuxtApp = useNuxtApp();
const { find } = useStrapiExtend();

const { home, specialOrders, specialOrdersDetails } = useLinker();
const { specialOrderSingleLink } = useSpecialOrderLinker();

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    et: "/eritellimused/[slug]",
    ru: "/specialnye-zakazy/[slug]",
    fi: "/erikoistilaukset/[slug]",
    lt: "/specialus-uzsakymai/[slug]",
    lv: "/specialie-pasutijumi/[slug]",
  },
});

const {
  data: pageData,
  pending,
  error: pageDataError,
} = await useSyncCachedAsyncData<SpecialOrderAttributes>(
  "specialOrderDetailsPageData" + route.params.slug,
  async () => {
    const { data } = await find<SpecialOrderAttributes>("special-orders", {
      populate: "deep,5",
      filters: { slug: route.params.slug },
      pagination: { limit: 1, start: 0 },
      localeBypass: true,
      // our strapi omits localizations by default; we need sibling slugs
      // to publish correct per-locale route params
      leaveLocalization: "1",
    });

    const item = data?.[0]?.attributes;

    if (!item) {
      throw createError({
        statusCode: 404,
        statusMessage: `Special order not found with current path: ${route.params.slug}`,
      });
    }

    // because localeBypass param was true, the resolved item may live under a
    // different slug in the active locale — send the visitor to its canonical
    // url. runWithContext is required: we are inside an async useAsyncData
    // handler, where the nuxt instance is otherwise unavailable and
    // navigateTo throws.
    if (item.slug !== route.params.slug) {
      await nuxtApp.runWithContext(() =>
        navigateTo(specialOrderSingleLink(item)),
      );
    }

    return trimLocalizations(item);
  },
);

// useAsyncData captures handler errors into `error` instead of propagating
// them, which would render an empty page with http 200. surfacing it here, in
// setup scope, is what makes ssr answer with a real 404.
wrapWithErrorHandler(pageDataError);

useLocalizedSlugParams(pageData);

const pageTitle = computed<string>(() => pageData.value?.title ?? "");

useSeoPack(
  computed(
    () =>
      ({
        title: pageTitle.value,
      }) as SeoPageMeta,
  ),
);
</script>

<style lang="scss">
.special-orders-details {
  &-breadcrumbs {
    @apply max-sm:pt-5 lg:pb-7 lg:pt-2;
  }

  &-title {
    @apply pb-3 pt-6 max-md:text-2xl md:py-8;
  }

  &-main {
    @apply max-sm:pt-3;
  }

  &-content {
    @apply mt-4 flex pb-10 max-lg:flex-col md:mt-5 md:justify-between md:gap-[80px];
  }

  .strapi-label {
    @apply mb-3 mt-6;
  }

  .special-order-field-label {
    @apply mt-2;
  }

  .form {
    @apply w-full;

    &-file-field {
      .zaproo-file-input__file {
        @apply h-10 md:relative;

        button {
          @apply md:absolute md:right-[calc(-131px-8px)];
        }
      }
    }

    &-field {
      @apply w-full pb-2;

      &-checkbox-group {
        @apply mt-2 flex flex-col gap-4;
      }

      &-group {
        @apply grid grid-cols-1 gap-2 md:grid-cols-2;
      }

      &-third {
        @apply grid grid-cols-1 gap-2 md:grid-cols-3;
      }

      .marquee {
        @apply mt-0.5 p-0;
      }
    }
  }

  .general-contacts-form {
    @apply mt-5 lg:mt-10;
  }

  .with-slider {
    @apply gap-0 lg:flex lg:gap-10;

    .main-slider {
      @apply hidden w-full lg:block lg:max-w-[375px];

      .slider-select-main-item {
        @apply lg:h-[375px];
      }
    }

    .form-slider {
      @apply w-full lg:hidden;

      .slider-select {
        .slider-select-main-item {
          @apply aspect-square;
        }

        .slider-select-thumbnail-item {
          @apply h-[89px] w-[92px] md:h-[179px] md:w-[179px];
        }
      }
    }
  }
}
</style>
